import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import theme from '../theme';
import { patchFollowUp } from '../../services/clientReportsApi';

const COLORS = theme.colors;

const STATUS_LABEL = {
  inadequate: 'Low accuracy',
  incomplete_cycle: 'Incomplete cycle',
  shadowed: 'Shadowed',
  unreachable: 'Unreachable',
};

/**
 * @param {{
 *   followUps: object[],
 *   loading?: boolean,
 *   error?: string,
 *   email: string,
 *   onChanged?: () => void,
 *   onOpenPack?: (historyId: string) => void,
 *   readOnly?: boolean,
 * }} props
 */
export default function FollowUpQueuePanel({
  followUps = [],
  loading,
  error,
  email,
  onChanged,
  onOpenPack,
  readOnly = false,
}) {
  const [filter, setFilter] = useState('open');
  const [completeTarget, setCompleteTarget] = useState(null);
  const [note, setNote] = useState('');
  const [busyId, setBusyId] = useState('');
  const [expanded, setExpanded] = useState(false);

  const filtered = followUps.filter((task) => {
    if (filter === 'all') return true;
    if (filter === 'overdue') {
      return (
        task.slaBreached &&
        (task.status === 'open' || task.status === 'acknowledged')
      );
    }
    if (filter === 'open') return task.status === 'open' || task.status === 'acknowledged';
    return task.status === filter;
  });

  const formatDue = (task) => {
    if (!task?.dueAt) return null;
    const due = new Date(task.dueAt);
    return due.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const patchStatus = async (id, status, verificationNote) => {
    setBusyId(id);
    try {
      await patchFollowUp(id, email, { status, verificationNote: verificationNote || '' });
      onChanged?.();
    } catch (e) {
      console.error('patch follow-up', e);
    } finally {
      setBusyId('');
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${COLORS.borderDefault}`,
        bgcolor: '#fff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        mb: 3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: expanded ? 2 : 0,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.textPrimary }}>
            Manual follow-up queue
          </Typography>
          <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, mt: 0.5, maxWidth: 560 }}>
            Surfaces that UV-C could not adequately reach (shadowed / unreachable accuracy proxies) —
            complete the manual wipe within the room turnover SLA and confirm on the disinfection
            record. Hygiene follow-up only — not a hospital-wide CAPA or policy queue.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {expanded ? (
            <TextField
              select
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="overdue">Overdue (SLA)</MenuItem>
              <MenuItem value="acknowledged">Acknowledged</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </TextField>
          ) : null}
          <Button
            size="small"
            variant="contained"
            endIcon={expanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
            onClick={() => setExpanded((v) => !v)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: `${theme.radius.md}px`,
              background: theme.gradients.primary,
              boxShadow: 'none',
              '&:hover': { background: theme.gradients.primaryHover, boxShadow: 'none' },
            }}
          >
            {expanded ? 'Close' : 'Open'}
          </Button>
        </Box>
      </Box>

      {expanded ? (
        <>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: COLORS.textSecondary, py: 2 }}>
              No manual follow-up tasks in this view.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {filtered.map((task) => (
                <Box
                  key={task.id}
                  sx={{
                    p: 2,
                    borderRadius: `${theme.radius.md}px`,
                    border: `1px solid ${COLORS.borderDefault}`,
                    bgcolor: COLORS.bgSecondary,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>
                        {task.objectLabel}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mt: 0.25 }}>
                        {task.roomName || '—'} · {task.reason || STATUS_LABEL[task.coverageStatus] || task.coverageStatus}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip size="small" label={task.status} sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
                      {task.slaBreached && ['open', 'acknowledged'].includes(task.status) ? (
                        <Chip
                          size="small"
                          label="Overdue"
                          sx={{
                            fontWeight: 700,
                            bgcolor: 'rgba(217, 119, 6, 0.15)',
                            color: '#b45309',
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>
                  {task.dueAt ? (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: task.slaBreached ? '#b45309' : COLORS.textSecondary,
                        mt: 0.75,
                        fontWeight: task.slaBreached ? 600 : 400,
                      }}
                    >
                      Due by {formatDue(task)}
                      {task.slaBreached ? ' — past turnover SLA' : ''}
                    </Typography>
                  ) : null}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
                    {task.historyId && onOpenPack ? (
                      <Button
                        size="small"
                        sx={{ textTransform: 'none' }}
                        onClick={() => onOpenPack(task.historyId)}
                      >
                        View pack
                      </Button>
                    ) : null}
                    {task.status === 'open' && !readOnly ? (
                      <Button
                        size="small"
                        disabled={busyId === task.id}
                        onClick={() => patchStatus(task.id, 'acknowledged')}
                        sx={{ textTransform: 'none' }}
                      >
                        Acknowledge
                      </Button>
                    ) : null}
                    {['open', 'acknowledged'].includes(task.status) && !readOnly ? (
                      <Button
                        size="small"
                        disabled={busyId === task.id}
                        onClick={() => {
                          setCompleteTarget(task);
                          setNote('Manual wipe completed');
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Complete
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      ) : null}

      <Dialog open={Boolean(completeTarget)} onClose={() => setCompleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Complete follow-up</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, mb: 2 }}>
            Confirm manual wipe for <strong>{completeTarget?.objectLabel}</strong>.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!note.trim() || busyId === completeTarget?.id}
            onClick={async () => {
              if (!completeTarget) return;
              await patchStatus(completeTarget.id, 'completed', note.trim());
              setCompleteTarget(null);
              setNote('');
            }}
          >
            Mark completed
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
